import React, { useContext, useState, useEffect, useRef, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { ProjectModalContext } from "./Service/ProjectModalContext";
import { getProjectTaskList, getProjectModule, getProjectSubModule, getProjectSegment, getProjectList } from "./Service/TaskService";
import { TostMsg } from "../../../App";
import { GetMasterTypeData } from "../../../Services/Global-Services/GlobalServices";
import { getEmployeeList } from "../EmployeeList/Service/employeeService.js";
import ERROR_LIST from "../../../Services/Global-Services/Hadle_Errors";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader.jsx";

const pillClass = (status) => {
  const map = {
    Active: "pill pill--active",
    Open: "pill pill--open",
    "In Progress": "pill pill--inprog",
    Review: "pill pill--review",
    Done: "pill pill--done",
    Closed: "pill pill--done",
    Archived: "pill pill--archived",
  };
  return map[status] || "pill pill--default";
};

function PastSprintsSection() {
  const location = useLocation();
  const { view } = location.state || {};
  const { selectedProject: contextProject } = useContext(ProjectModalContext);
  const toster = useContext(TostMsg);

  const CompletedSprints = contextProject?.ProjectSprints?.CompletedSprints || [];
  const currentProjectId = CompletedSprints[0]?.ProjectId || contextProject?.Id || null;
  const projectName = CompletedSprints[0]?.ProjectName || contextProject?.ProjectName || "OrgGenie HRMS";

  const [selectedSprint, setSelectedSprint] = useState(null);
  const [showTaskView, setShowTaskView] = useState(false);
  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(false);

  // ═══════ FILTER STATES ═══════
  const [filterOpen, setFilterOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedAssingToName, setSelectedAssingToName] = useState([]);
  const [selectedType, setSelectedType] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState([]);
  const [selectedSeverity, setSelectedSeverity] = useState([]);
  const [selectedFilterProject, setSelectedFilterProject] = useState(null);
  const [selectedModule, setSelectedModule] = useState([]);
  const [selectedSubModule, setSelectedSubModule] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("ASC");

  // Master data
  const [taskStatusType, setTaskStatusType] = useState([]);
  const [taskType, setTaskType] = useState([]);
  const [taskPriorityType, setTaskPriorityType] = useState([]);
  const [taskSeverityType, setTaskSeverityType] = useState([]);
  const [employeDetails, setEmployeDetails] = useState([]);
  const [allProject, setAllProject] = useState([]);
  const [taskModuleType, setTaskModuleType] = useState([]);
  const [taskSubModuleType, setTaskSubModuleType] = useState([]);
  const [taskSegmentType, setTaskSegmentType] = useState([]);

  const debounceTimerRef = useRef(null);
  const selectedFilterProjectRef = useRef(selectedFilterProject);
  const selectedSprintRef = useRef(selectedSprint);

  let userDetails = JSON.parse(localStorage.getItem("User") || "{}");
  const getValue = (val) => (val ?? "N/A") || "N/A";
  const calcProgress = (completed, total) => (!total ? 0 : Math.round((completed / total) * 100));

  useEffect(() => {
    getMasterData();
  }, []);

  // Pre-select project filter from context
  useEffect(() => {
    if (currentProjectId) {
      setSelectedFilterProject(currentProjectId);
      selectedFilterProjectRef.current = currentProjectId;
      getMasterTaskModulesCategory(currentProjectId);
      getMasterTaskSegmentCategory(currentProjectId);
    }
  }, [currentProjectId]);

  // Re-fetch tasks whenever filters change (only when a sprint is selected)
  useEffect(() => {
    if (selectedSprintRef.current) {
      fetchTasksForSprint(selectedSprintRef.current);
    }
  }, [
    selectedStatus, selectedAssingToName, selectedType, selectedPriority,
    selectedSeverity, selectedModule, selectedSubModule, selectedSegment,
    searchText, sortBy, sortOrder,
  ]);

  useEffect(() => {
    selectedFilterProjectRef.current = selectedFilterProject;
  }, [selectedFilterProject]);

  useEffect(() => {
    selectedSprintRef.current = selectedSprint;
  }, [selectedSprint]);

  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const sprintDuration = (s, e) => {
    if (!s || !e) return "N/A";
    const a = new Date(s), b = new Date(e);
    return isNaN(a) || isNaN(b) ? "N/A" : `${Math.ceil((b - a) / 86400000)} days`;
  };

  const initials = (name) =>
    name ? name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() : "?";

  // ═══════ MASTER DATA ═══════
  async function getMasterData() {
    try {
      const [statusData, typeData, priorityData, severityData, employeeData, projectData] = await Promise.all([
        GetMasterTypeData({ organisationId: userDetails?.OrgId, masterType: "TaskStatus" }),
        GetMasterTypeData({ organisationId: userDetails?.OrgId, masterType: "TaskType" }),
        GetMasterTypeData({ organisationId: userDetails?.OrgId, masterType: "TaskPriority" }),
        GetMasterTypeData({ organisationId: userDetails?.OrgId, masterType: "TaskSeverity" }),
        getEmployeeList({
          OrganisationId: userDetails?.OrgId,
          LoggedInEmployeeId: userDetails?.EmpId,
          pageNumber: 1,
          pageSize: 10000,
        }),
        getProjectList({
          Id: 0, StartDate: "", EndDate: "", ProjectStatusId: "",
          PageNumber: 1, PageSize: 1000, Search: "", ProjectTypeId: "",
          ClientId: "", SortBy: "", SortOrder: "",
        }),
      ]);
      setTaskStatusType(statusData?.Data || []);
      setTaskType(typeData?.Data || []);
      setTaskPriorityType(priorityData?.Data || []);
      setTaskSeverityType(severityData?.Data || []);
      setEmployeDetails(employeeData?.Data || []);
      setAllProject(projectData?.Data?.ProjectList || []);
    } catch (e) {
      toster.current.error(ERROR_LIST?.MasterDataType || "Failed to load filter data");
    }
  }

  async function getMasterTaskModulesCategory(projectId = 0) {
    if (!projectId) return;
    try {
      const data = await getProjectModule({ ProjectId: Number(projectId) });
      setTaskModuleType(data?.Data || []);
    } catch (e) {
      toster.current.error(e?.response?.data?.Message || "Failed to load modules");
    }
  }

  async function getMasterTaskSubModuleCategory(projectId = 0, moduleId = 0) {
    if (!projectId || !moduleId) return;
    try {
      const data = await getProjectSubModule({ ProjectId: Number(projectId), ModuleId: Number(moduleId) });
      setTaskSubModuleType(data?.Data || []);
    } catch (e) {
      toster.current.error(e?.response?.data?.Message || "Failed to load sub-modules");
    }
  }

  async function getMasterTaskSegmentCategory(projectId = 0) {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await getProjectSegment({ ProjectId: Number(projectId) });
      setLoading(false);
      setTaskSegmentType(data?.Data || []);
    } catch (e) {
      setLoading(false);
      toster.current.error(e?.response?.data?.Message || "Failed to load segments");
    }
  }

  // ═══════ FILTER HELPERS ═══════
  const hasActiveFilters =
    searchText ||
    selectedStatus?.length > 0 ||
    selectedPriority?.length > 0 ||
    selectedSeverity?.length > 0 ||
    selectedModule?.length > 0 ||
    selectedSubModule?.length > 0 ||
    selectedSegment?.length > 0 ||
    selectedAssingToName?.length > 0 ||
    selectedType?.length > 0 ||
    sortBy;

  const toggleSortOrder = () => setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));

  const clearFilters = () => {
    setInputText("");
    setSearchText("");
    setSelectedStatus([]);
    setSelectedPriority([]);
    setSelectedSeverity([]);
    setSelectedModule([]);
    setSelectedSubModule([]);
    setSelectedSegment([]);
    setSelectedAssingToName([]);
    setSelectedType([]);
    setSortBy("");
    setSortOrder("ASC");
    setSelectedFilterProject(currentProjectId);
    selectedFilterProjectRef.current = currentProjectId;
    if (currentProjectId) {
      getMasterTaskModulesCategory(currentProjectId);
      getMasterTaskSegmentCategory(currentProjectId);
    } else {
      setTaskModuleType([]);
      setTaskSubModuleType([]);
      setTaskSegmentType([]);
    }
  };

  const SORT_OPTIONS = [
    { label: "Modified On (Default)", value: "" },
    { label: "Assigned To", value: "AssignedToName" },
    { label: "Priority", value: "PriorityId" },
    { label: "Severity", value: "SeverityId" },
    { label: "Type", value: "TaskTypeId" },
    { label: "Module", value: "ModuleName" },
    { label: "Sub Module", value: "SubModuleName" },
    { label: "Status", value: "StatusId" },
    { label: "Created On", value: "CreatedOn" },
  ];

  // ═══════ TASK FETCHING ═══════
  async function fetchTasksForSprint(sprint) {
    if (!sprint) return;
    setLoading(true);
    const params = {
      RaisedByEmpId: "",
      AssignedToId: selectedAssingToName?.join(",") || "",
      TaskId: 0,
      StatusId: selectedStatus?.join(",") || "",
      ModuleId: selectedModule?.join(",") || "",
      SubModuleId: selectedSubModule?.join(",") || "",
      PriorityId: selectedPriority?.join(",") || "",
      SeverityId: selectedSeverity?.join(",") || "",
      Search: searchText || "",
      FilterStartDate: "",
      FilterEndDate: "",
      PageNumber: 0,
      PageSize: 0,
      IsDeleted: 0,
      SprintId: 0,
      TaskNumber: "",
      TaskGUID: "",
      ProjectId: (sprint?.ProjectId).toString(),
      ParentTaskId: 0,
      TaskTypeId: selectedType?.join(",") || "",
      SegmentId: selectedSegment?.join(",") || "",
      SortBy: sortBy || "",
      SortOrder: sortOrder || "",
    };
    try {
      const data = await getProjectTaskList(params);
      const list = data?.Data?.OrgEmpTasks || [];
      setLoading(false);
      // Filter to only tasks belonging to this specific sprint
      const filtered = list.filter((t) => Number(t.SprintId) === Number(sprint?.Id));
      setTaskList(filtered);
    } catch (err) {
      setLoading(false);
      toster.current.error(err?.response?.data?.Message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function getProjectTask(sprint) {
    setSelectedSprint(sprint);
    selectedSprintRef.current = sprint;
    setShowTaskView(true);
    await fetchTasksForSprint(sprint);
  }

  function handleViewAll() {
    setShowTaskView(false);
    setSelectedSprint(null);
    selectedSprintRef.current = null;
    setTaskList([]);
    clearFilters();
  }

  return (
    <div className="pss-area">

      {/* ── welcome bar ── */}
      <div className="task-top-pane">
        <i className="fa fa-thumb-tack i-ico pss-pin-icon" aria-hidden="true"></i>
        <div className="task-top-pane-title">
          <h4>Welcome to OrgGenie</h4>
          <p>Manage your tasks, projects, and workspaces in one place.</p>
        </div>
      </div>

      <div className="tm-main-area">

        {/* ── page header ── */}
        <div className="page-head pss-page-head">
          <div>
            {/* <nav className="pss-breadcrumb">
              <span>Projects</span>
              <span className="pss-bc-sep">›</span>
              <span>{projectName}</span>
              <span className="pss-bc-sep">›</span>
              {showTaskView && selectedSprint ? (
                <>
                  <button className="pss-bc-btn" onClick={handleViewAll}>Past Sprints</button>
                  <span className="pss-bc-sep">›</span>
                  <span className="pss-bc-current">{selectedSprint.SprintName}</span>
                </>
              ) : (
                <span className="pss-bc-current">Past Sprints</span>
              )}
            </nav> */}

            <h2 className="pss-page-title">
              {showTaskView && selectedSprint ? selectedSprint.SprintName : "Past Sprints"}
            </h2>
            <p className="pss-page-sub">
              {showTaskView && selectedSprint
                ? `${taskList.length} task${taskList.length !== 1 ? "s" : ""} in this sprint`
                : `${CompletedSprints.length} sprint${CompletedSprints.length !== 1 ? "s" : ""} completed · ${projectName}`}
            </p>
          </div>

          <div className="pss-head-actions">
            {showTaskView && (
              <button className="pss-btn-viewall" onClick={handleViewAll} title="Back to all sprints">
                <svg width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                </svg>
                All Sprints
              </button>
            )}
            <Link to="/Project/Dashboard" className="pss-btn-back" title="Back to Dashboard">
              <svg width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
              </svg>
            </Link>
          </div>
        </div>

        {/* ══════════ SPRINT GRID ══════════ */}
        {!showTaskView && (
          <div className="pss-scroll-area">
            {CompletedSprints.length === 0 ? (
              <div className="pss-empty">
                <i className="fa-solid fa-flag-checkered pss-empty-icon"></i>
                <p>No completed sprints yet</p>
              </div>
            ) : (
              <div className="pss-sprint-grid">
                {CompletedSprints.map((sprint) => {

                  const dur = sprintDuration(sprint.PlannedStartDate, sprint.PlannedEndDate);

                  return (
                    <div className="pss-card" key={sprint.Guid}>
                      <div className="pss-card-accent"></div>
                      <div className="pss-card-body">
                        <div className="pss-card-head-row">
                          <div>

                            <div className="pss-sprint-name-wrap">
                              <div className="pss-sprint-num">
                                S{String(sprint.SprintNumber).padStart(2, "0")}
                              </div>
                              <div>
                                <p className="pss-sprint-name">{sprint.SprintName}</p>
                                <p className="pss-sprint-dates">
                                  {formatDate(sprint.PlannedStartDate)}
                                  <span className="pss-arrow">→</span>
                                  {formatDate(sprint.PlannedEndDate)}
                                  <span className="pss-dot">·</span>
                                  {dur}
                                </p>
                              </div>
                            </div>

                          </div>
                          <span className="pss-done-badge">
                            <i className="fa-solid fa-circle-check"></i>
                            Completed
                          </span>
                        </div>
                        <div className="pss-meta-row">
                          <div className="pss-meta-box">
                            <span className="pss-meta-label">Tasks</span>
                            <span className="pss-meta-val">{sprint.ActiveTasksInSprint} / {sprint.TotalTasksInSprint}</span>
                          </div>
                          <div className="pss-meta-box">
                            <span className="pss-meta-label">Completion</span>
                            <span className="pss-meta-val pss-meta-val--green">{calcProgress(sprint.ActiveTasksInSprint, sprint.TotalTasksInSprint)}%</span>
                          </div>
                          <div className="pss-meta-box">
                            <span className="pss-meta-label">Duration</span>
                            <span className="pss-meta-val">{dur}</span>
                          </div>
                        </div>

                        <div className="pss-prog-wrap">
                          <span className="pss-meta-label">Progress</span>
                          <div className="pss-prog-bar">
                            <div className="pss-prog-fill" style={{ width: `${calcProgress(sprint.ActiveTasksInSprint, sprint.TotalTasksInSprint)}%` }}></div>
                          </div>
                        </div>

                        {sprint.SprintGoal && (
                          <div className="pss-goal-chip">
                            <i className="fa-solid fa-flag"></i>
                            {sprint.SprintGoal}
                          </div>
                        )}
                      </div>

                      <div className="pss-card-foot">
                        <button className="pss-view-tasks-btn" onClick={() => getProjectTask(sprint)}>
                          <i className="fa-solid fa-list-check"></i>
                          View Tasks
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════ TASK VIEW ══════════ */}
        {showTaskView && (
          <div className="pss-task-view">

            {/* ── Filter Section ── */}
            <div className="collapsible-section" id="filterSection" onClick={() => setFilterOpen(!filterOpen)}>
              <div className="section-toggle">
                <div className="section-toggle-icon icon-filter" onClick={() => setFilterOpen(!filterOpen)}>
                  <svg viewBox="0 0 28 28" width="28" height="28" xmlns="http://www.w3.org/2000/svg" fill="none">
                    <rect width="28" height="28" rx="8" fill="#f0fdf4" />
                    <path d="M7 9.5h14M9.5 14h9M12 18.5h4" stroke="#15803d" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
                <span className="section-label">Filters</span>

                <div className="sort-bydiv" onClick={(e) => e.stopPropagation()}>
                  <label style={{ fontSize: "11px", color: "var(--muted)", whiteSpace: "nowrap", marginBottom: "0" }}>Sort By:</label>
                  <select
                    className="fs"
                    style={{ width: "auto" }}
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setSortOrder("ASC"); }}
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-ghost btn-icon btn-sm"
                    title={sortOrder === "ASC" ? "Ascending — click for Descending" : "Descending — click for Ascending"}
                    onClick={toggleSortOrder}
                    disabled={!sortBy}
                    style={{
                      opacity: sortBy ? 1 : 0.35,
                      cursor: sortBy ? "pointer" : "not-allowed",
                      fontSize: "13px", width: "30px", height: "30px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: "1px solid var(--border)", borderRadius: "var(--radius)",
                      background: sortBy ? "var(--card)" : "transparent", transition: "all 0.15s",
                    }}
                  >
                    {sortOrder === "ASC" ? <i className="fa-solid fa-arrow-up-a-z"></i> : <i className="fa-solid fa-arrow-down-z-a"></i>}
                  </button>
                </div>
              </div>

              <div className={`section-body ${filterOpen ? "open" : ""}`} id="filterBody">
                <div className="section-body-inner" onClick={(e) => e.stopPropagation()}>
                  <div className="toolbar my-toolbar">
                    <div className="toolbar-sepdiv">
                      {/* Search */}
                      <div className="sw" style={{ flex: "2 1 220px", minWidth: 180 }}>
                        <i className="fa-solid fa-magnifying-glass"></i>
                        <input
                          className="si"
                          placeholder="Search tasks, ID…"
                          value={inputText}
                          onChange={(e) => {
                            const value = e.target.value;
                            setInputText(value);
                            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                            debounceTimerRef.current = setTimeout(() => setSearchText(value), 1000);
                          }}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div className="tsep"></div>

                      <MultiSelect value={selectedStatus} onChange={(e) => setSelectedStatus(e.value)}
                        options={taskStatusType} optionLabel="Name" optionValue="Id" placeholder="All Status" filter />

                      <MultiSelect value={selectedAssingToName} onChange={(e) => setSelectedAssingToName(e.value)}
                        options={employeDetails} optionLabel="FullName" optionValue="Id" placeholder="Assigned To"
                        filter style={{ flex: "1 1 140px", minWidth: 130 }} />

                      <MultiSelect value={selectedType} onChange={(e) => setSelectedType(e.value)}
                        options={taskType} optionLabel="Name" optionValue="Id" placeholder="All Types" filter />

                      <MultiSelect value={selectedPriority} onChange={(e) => setSelectedPriority(e.value)}
                        options={taskPriorityType} optionLabel="Name" optionValue="Id" placeholder="All Priority" filter />

                      <MultiSelect value={selectedSeverity} onChange={(e) => setSelectedSeverity(e.value)}
                        options={taskSeverityType} optionLabel="Name" optionValue="Id" placeholder="All Severity" filter />

                      <Dropdown
                        value={selectedFilterProject ?? null}
                        options={allProject}
                        optionLabel="Name"
                        optionValue="Id"
                        placeholder="All Projects"
                        disabled={selectedFilterProject}
                        filter
                        showClear
                        style={{ flex: "1 1 150px", minWidth: 140 }}
                        onChange={(e) => {
                          const val = e.value ?? null;
                          setSelectedFilterProject(val);
                          selectedFilterProjectRef.current = val;
                          setSelectedModule([]);
                          setSelectedSubModule([]);
                          setSelectedSegment([]);
                          setTaskModuleType([]);
                          setTaskSubModuleType([]);
                          setTaskSegmentType([]);
                          if (val) {
                            getMasterTaskModulesCategory(val);
                            getMasterTaskSegmentCategory(val);
                          }
                        }}
                      />

                      <MultiSelect
                        value={selectedModule}
                        onChange={(e) => {
                          const val = e.value;
                          setSelectedModule(val);
                          setSelectedSubModule([]);
                          setTaskSubModuleType([]);
                          if (val?.length === 1)
                            getMasterTaskSubModuleCategory(selectedFilterProject, val[0]);
                        }}
                        options={taskModuleType} optionLabel="Name" optionValue="Id"
                        placeholder="Select Module" filter disabled={!selectedFilterProject}
                      />

                      <MultiSelect value={selectedSubModule} onChange={(e) => setSelectedSubModule(e.value)}
                        options={taskSubModuleType} optionLabel="Name" optionValue="Id"
                        placeholder="Select Sub-Module" filter disabled={!selectedModule.length} />

                      <MultiSelect value={selectedSegment} onChange={(e) => setSelectedSegment(e.value)}
                        options={taskSegmentType} optionLabel="Name" optionValue="Id"
                        placeholder="Select Segment" filter disabled={!selectedFilterProject} />

                      <div className="tsep"></div>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={clearFilters}
                        disabled={!hasActiveFilters}
                        title="Clear all filters"
                        style={{ opacity: hasActiveFilters ? 1 : 0.4, cursor: hasActiveFilters ? "pointer" : "not-allowed" }}
                      >
                        <i className="fa-solid fa-xmark"></i> Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Task Table ── */}
            {loading ? (
              <div className="pss-loading">
                <i className="fa-solid fa-spinner fa-spin"></i>
                Loading tasks…
              </div>
            ) : (
              <div className="height-body-equal2">
                <table className="dtbl">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Task ID</th>
                      <th>Type · Priority · Severity</th>
                      <th>Status</th>
                      <th>Assigned To</th>
                      <th>Due · Est</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taskList.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="pss-no-data">
                          <i className="fa-solid fa-inbox pss-no-data-icon"></i>
                          No tasks found for this sprint
                        </td>
                      </tr>
                    ) : (
                      taskList.map((task) => {
                        const titleTrimmed =
                          task.TaskTitle?.length > 45 ? task.TaskTitle.slice(0, 45) + "…" : task.TaskTitle;
                        return (
                          <tr key={task?.Id} className="pss-tr">
                            <td className="pss-td pss-td--title">
                              <p className="pss-task-title">{getValue(titleTrimmed)}</p>
                              <p className="pss-task-meta">
                                <span title={task.IsPublic ? "Public" : "Private"}>
                                  <i className={`fa-solid ${task.IsPublic ? "fa-lock-open pss-icon--red" : "fa-lock pss-icon--muted"}`}></i>
                                </span>
                                <span>{getValue(task.ProjectName)}</span>
                                {task.ModuleName && <><span className="pss-meta-sep">·</span><span>{task.ModuleName}</span></>}
                                {task.SubModuleName && <><span className="pss-meta-sep">-</span><span>{task.SubModuleName}</span></>}
                                {task.CreatedByFullName && (
                                  <>
                                    <span className="pss-meta-sep">·</span>
                                    <img src={task.CreatedByProfileImage || "/images/placeholder_profile.png"}
                                      onError={(e) => (e.target.src = "/images/placeholder_profile.png")}
                                      className="pss-mini-avatar" />
                                    <span>{task.CreatedByFullName}</span>
                                  </>
                                )}
                              </p>
                            </td>
                            <td className="pss-td">
                              <span className="pss-task-num">{getValue(task.TaskNumber)}</span>
                            </td>
                            <td className="pss-td pss-td--nowrap">
                              <div className="pss-type-name">{getValue(task.TaskTypeName)}</div>
                              <div className="pss-type-sub">{getValue(task.PriorityName)} · {getValue(task.SeverityName)}</div>
                            </td>
                            <td className="pss-td">
                              <span className={pillClass(task.StatusName)}>{getValue(task.StatusName)}</span>
                            </td>
                            <td className="pss-td">
                              <div className="pss-assignee">
                                {task.AssignToProfileImage ? (
                                  <img src={task.AssignToProfileImage}
                                    onError={(e) => (e.target.src = "/images/placeholder_profile.png")}
                                    className="pss-avatar" />
                                ) : (
                                  <div className="pss-avatar-placeholder">{initials(task.AssignToName)}</div>
                                )}
                                <span className="pss-assignee-name">{getValue(task.AssignToName)}</span>
                              </div>
                            </td>
                            <td className="pss-td pss-td--nowrap">
                              <div className="pss-due-date">
                                {task.DueDate ? new Date(task.DueDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
                              </div>
                              <div className="pss-est-hrs">{task.EstimatedHours ? `${task.EstimatedHours}h est.` : "—"}</div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {(loading) && (
        <Suspense>
          <ComponentLoader />
        </Suspense>
      )}
    </div>
  );
}

export default PastSprintsSection;